import { useEffect, useMemo, useState, type ComponentType } from 'react'
import { Outlet, useLocation, useNavigate, NavLink, Link } from 'react-router-dom'
import { Shield, Users, ScrollText, BarChart3, AlertTriangle, Lock, Settings, LogOut, Mail, Activity, Megaphone, UserCog, Globe } from 'lucide-react'
import { getAdminRole, canAccess, type AdminRole } from '@/lib/auth'

const navItems: { path: string; label: string; icon: ComponentType<{ className?: string }>; minRole?: AdminRole }[] = [
  { path: '/', label: 'Overview', icon: Shield },
  { path: '/accounts', label: 'Accounts', icon: Users },
  { path: '/waitlist', label: 'Waitlist', icon: Mail },
  { path: '/audit-log', label: 'Audit Log', icon: ScrollText, minRole: 'platform_admin' },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/abuse-queue', label: 'Abuse Queue', icon: AlertTriangle },
  { path: '/domains', label: 'Domains', icon: Globe },
  { path: '/status', label: 'Status', icon: Activity },
  { path: '/broadcasts', label: 'Broadcasts', icon: Megaphone, minRole: 'founder' },
  { path: '/superadmins', label: 'Superadmins', icon: UserCog, minRole: 'founder' },
  { path: '/security', label: 'Security', icon: Lock },
  { path: '/settings', label: 'Settings', icon: Settings },
]

export function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [role, setRole] = useState<AdminRole | null>(getAdminRole())

  useEffect(() => {
    const token = localStorage.getItem('admin_token')
    if (!token && location.pathname !== '/login') {
      navigate('/login')
    }
    setRole(getAdminRole())
  }, [location.pathname, navigate])

  const visibleNavItems = useMemo(
    () => navItems.filter((item) => !item.minRole || canAccess(role, item.minRole)),
    [role]
  )

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/login')
  }

  return (
    <div className="flex h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside className="w-64 border-r bg-card flex flex-col">
        <div className="p-6 border-b">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="PurpleToad Mail" className="h-8 w-8" />
            <h1 className="text-xl font-bold tracking-tight">PurpleToad Mail Admin</h1>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 w-full rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 border-b flex items-center px-6 bg-card">
          <div className="ml-auto text-sm text-muted-foreground">
            Admin Console
          </div>
        </header>
        <div className="flex-1 overflow-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
